import React, { useState, useEffect, useRef } from 'react';
import Lenis from 'lenis';
import Nav from './components/Nav';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import { Suspense, lazy } from 'react';

/**
 * A lazy route that can be resolved ahead of render.
 *
 * Plain React.lazy breaks hydration of a prerendered page: the chunk is not
 * loaded on the first client render, so React shows the Suspense fallback
 * where the snapshot has real content, hits a text mismatch and re-renders
 * that subtree from scratch. Preloading the entry route's chunk before
 * hydrating (see preloadInitialPage below) lets `Resolved` be used directly,
 * so the first render matches the prerendered HTML exactly.
 */
const lazyPage = (factory) => {
  const Lazy = lazy(factory);
  Lazy.preload = () =>
    factory().then((mod) => {
      Lazy.Resolved = mod.default;
      return mod;
    });
  return Lazy;
};

const HomePage = lazyPage(() => import('./pages/HomePage'));
const CRMPage = lazyPage(() => import('./pages/CRMPage'));
const LeadsPage = lazyPage(() => import('./pages/LeadsPage'));
const XFactorPage = lazyPage(() => import('./pages/XFactorPage'));
const AboutPage = lazyPage(() => import('./pages/AboutPage'));
const PricingPage = lazyPage(() => import('./pages/PricingPage'));
const ComparisonPage = lazyPage(() => import('./pages/ComparisonPage'));
const PrivacyPolicyPage = lazyPage(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazyPage(() => import('./pages/TermsOfServicePage'));
const FAQPage = lazyPage(() => import('./pages/FAQPage'));
const Blog1 = lazyPage(() => import('./pages/blog/Blog1'));
const Blog2 = lazyPage(() => import('./pages/blog/Blog2'));
const Blog3 = lazyPage(() => import('./pages/blog/Blog3'));
const Blog4 = lazyPage(() => import('./pages/blog/Blog4'));
const Blog5 = lazyPage(() => import('./pages/blog/Blog5'));
const Blog6 = lazyPage(() => import('./pages/blog/Blog6'));
const Blog7 = lazyPage(() => import('./pages/blog/Blog7'));
const Blog8 = lazyPage(() => import('./pages/blog/Blog8'));
const Blog9 = lazyPage(() => import('./pages/blog/Blog9'));
const BlogIndex = lazyPage(() => import('./pages/blog/BlogIndex'));
import BookTrialModal from './components/BookTrialModal';

function DotCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const gap = 24;
    const dpr = window.devicePixelRatio || 1;

    // Everything about this canvas — position as well as size — is applied
    // here rather than through React. The effect has to set width/height
    // imperatively anyway (they depend on the viewport and DPR), and a
    // partially React-owned element cannot survive hydration: the prerendered
    // snapshot would carry build-time pixel values that React's first render
    // does not produce. Owning all of it in one place keeps React's output a
    // bare <canvas>, which matches the snapshot exactly.
    Object.assign(canvas.style, {
      position: 'fixed',
      top: '0px',
      left: '0px',
      pointerEvents: 'none',
      zIndex: 0,
    });

    function draw() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.scale(dpr, dpr);
      const cols = Math.ceil(w / gap) + 1;
      const rows = Math.ceil(h / gap) + 1;
      const ox = (w - (cols - 1) * gap) / 2;
      const oy = (h - (rows - 1) * gap) / 2;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          ctx.beginPath();
          ctx.arc(ox + c * gap, oy + r * gap, 1, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(0,0,0,0.10)';
          ctx.fill();
        }
      }
    }

    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, []);

  return <canvas ref={canvasRef} />;
}

const PAGES = {
  home: HomePage,
  crm: CRMPage,
  leads: LeadsPage,
  xfactor: XFactorPage,
  about: AboutPage,
  pricing: PricingPage,
  comparison: ComparisonPage,
  privacy: PrivacyPolicyPage,
  terms: TermsOfServicePage,
  faq: FAQPage,
  'blog-1': Blog1,
  'blog-2': Blog2,
  'blog-3': Blog3,
  'blog-4': Blog4,
  'blog-5': Blog5,
  'blog-6': Blog6,
  'blog-7': Blog7,
  'blog-8': Blog8,
  'blog-9': Blog9,
  'blog': BlogIndex,
};

const PATH_TO_PAGE = {
  '/CRM': 'crm',
  '/lead-platform': 'leads',
  '/The-X-Factor': 'xfactor',
  '/About-us': 'about',
  '/Pricing': 'pricing',
  '/comparison': 'comparison',
  '/privacy-policy': 'privacy',
  '/terms-of-service': 'terms',
  '/faq': 'faq',
  '/blog/ai-crm-for-real-estate': 'blog-1',
  '/blog/how-to-generate-property-leads': 'blog-2',
  '/blog/best-real-estate-crm-india': 'blog-3',
  '/blog/whatsapp-crm-for-real-estate': 'blog-4',
  '/blog/rera-compliant-crm-software': 'blog-5',
  '/blog/reduce-junk-leads-real-estate': 'blog-6',
  '/blog/ai-voice-calling-real-estate': 'blog-7',
  '/blog/real-estate-crm-vs-spreadsheet': 'blog-8',
  '/blog/real-estate-lead-management-india': 'blog-9',
  '/blog': 'blog',
};

const PAGE_TO_PATH = {
  crm: '/CRM',
  leads: '/lead-platform',
  xfactor: '/The-X-Factor',
  about: '/About-us',
  pricing: '/Pricing',
  comparison: '/comparison',
  privacy: '/privacy-policy',
  terms: '/terms-of-service',
  faq: '/faq',
  'blog-1': '/blog/ai-crm-for-real-estate',
  'blog-2': '/blog/how-to-generate-property-leads',
  'blog-3': '/blog/best-real-estate-crm-india',
  'blog-4': '/blog/whatsapp-crm-for-real-estate',
  'blog-5': '/blog/rera-compliant-crm-software',
  'blog-6': '/blog/reduce-junk-leads-real-estate',
  'blog-7': '/blog/ai-voice-calling-real-estate',
  'blog-8': '/blog/real-estate-crm-vs-spreadsheet',
  'blog-9': '/blog/real-estate-lead-management-india',
  'blog': '/blog',
};

/**
 * Resolves a URL path to a page key.
 *
 * The lookup is normalized rather than exact because a trailing slash used to
 * silently fall through to the homepage. That is not hypothetical: /lead-platform
 * collides with the real asset directory public/lead-platform/, so static hosts
 * redirect it to /lead-platform/ — which meant the Leads Platform URL rendered
 * homepage content, and the prerenderer baked that mistake into the snapshot.
 * Any inbound link with a trailing slash hit the same bug.
 */
const resolvePage = (rawPath) => {
  const path = rawPath.length > 1 ? rawPath.replace(/\/+$/, '') : rawPath;

  if (PATH_TO_PAGE[path]) return PATH_TO_PAGE[path];

  // Case-insensitive fallback so /crm and /pricing resolve rather than 404
  // into the homepage. Hosting-level redirects send these to the canonical
  // casing; this keeps client-side navigation correct if one is ever missed.
  const lower = path.toLowerCase();
  const match = Object.keys(PATH_TO_PAGE).find((p) => p.toLowerCase() === lower);
  return match ? PATH_TO_PAGE[match] : 'home';
};

const getInitialPage = () => {
  const path = window.location.pathname;

  if (path === '/Leads-Platform' || path.toLowerCase() === '/leads-platform') {
    window.history.replaceState({}, '', '/lead-platform');
    return 'leads';
  }

  return resolvePage(path);
};

/**
 * Loads the chunk for whatever route the browser is currently on. Call and
 * await this before hydrating — see src/index.js.
 */
export function preloadInitialPage() {
  const entry = PAGES[resolvePage(window.location.pathname)];
  return entry && entry.preload ? entry.preload() : Promise.resolve();
}

export default function App() {
  const [activePage, setActivePage] = useState(getInitialPage);
  const [isBookTrialOpen, setIsBookTrialOpen] = useState(false);

  const navigate = (page) => {
    setActivePage(page);
    const path = PAGE_TO_PATH[page] || '/';
    window.history.pushState({ page }, '', path);
    if (window.lenis) {
      window.lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setActivePage(resolvePage(window.location.pathname));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const entry = PAGES[activePage] || HomePage;
  // Use the eagerly-resolved component when the chunk is already in memory so
  // the initial render never flashes the Suspense fallback over prerendered
  // markup. Later client-side navigations fall back to the lazy component.
  const PageComponent = entry.Resolved || entry;

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), 
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    lenis.on('scroll', (e) => {
      // Find all elements with data-speed attribute and apply translate
      const parallaxElements = document.querySelectorAll('[data-speed]');
      parallaxElements.forEach((el) => {
        const speed = parseFloat(el.getAttribute('data-speed'));
        if (!isNaN(speed)) {
          const y = e.animatedScroll * speed;
          el.style.setProperty('--parallax-y', `${y}px`);
        }
      });
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);
    window.lenis = lenis;

    return () => {
      lenis.destroy();
      delete window.lenis;
    };
  }, []);

  return (
    <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <DotCanvas />
      <ScrollProgress />
      <Nav activePage={activePage} onNavigate={navigate} onTrialClick={() => setIsBookTrialOpen(true)} />
      <main style={{ flex: 1, paddingTop: 0 }}>
        {/*
          The Suspense boundary is omitted once the chunk is resolved. This is
          not just an optimisation: react-snap snapshots a client-rendered DOM,
          which has none of the <!--$--> boundary markers that hydrateRoot
          expects around a Suspense boundary. Hydrating a <Suspense> against
          that markup mismatches even when the children are already loaded, and
          React then discards the whole prerendered tree.
        */}
        {entry.Resolved ? (
          <PageComponent onNavigate={navigate} key={activePage} onTrialClick={() => setIsBookTrialOpen(true)} />
        ) : (
          <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#5E5E72', fontFamily: 'var(--font-sans)', fontSize: 14 }}>Loading...</div>}>
            <PageComponent onNavigate={navigate} key={activePage} onTrialClick={() => setIsBookTrialOpen(true)} />
          </Suspense>
        )}
      </main>
      <Footer onNavigate={navigate} onTrialClick={() => setIsBookTrialOpen(true)} />
      <BookTrialModal 
        isOpen={isBookTrialOpen} 
        onClose={() => setIsBookTrialOpen(false)} 
        onNavigate={navigate}
      />
    </div>
  );
}
